import { ActionIcon, Box, Button, Group, Stack, Text } from '@mantine/core';
import { POST_SESSION_PRIMARY_BG } from '../../../../components/session/postSessionCard';
import { ContentCard } from '../../../../components/common/ContentCard';
import { CheckSquare, ChevronDown, ChevronUp, Info } from 'react-feather';
import type { PostSessionParticipant } from '../../../../services/api/coachPostSession';
import type { AttendanceDraftMap } from '../../../../hooks/useCoachPostSession';
import { useAttendanceSectionCollapse } from '../../../../hooks/useAttendanceSectionCollapse';
import ParticipantAttendanceRow, { ATTENDANCE_PREFILL_NOTE } from './ParticipantAttendanceRow';

/**
 * MED-794 / MED-979 — Attendance & Engagement card.
 *
 * Left column, primary card. Per-participant rows for Present/Partial/Absent
 * with the below-row expansion panel (Reason / Engagement / Notes per the
 * MED-979 spec). Save button commits via the existing
 * `coachPortalApi.markAttendance` endpoint.
 *
 * MED-979: no self-row in the coach view — the coach is not a cohort
 * participant, so there is no "You" badge.
 *
 * MED-1077: collapses to a summary line ("6 present · 1 partial · 1 absent")
 * once attendance is complete AND saved, with an expand chevron — same
 * `useAttendanceSectionCollapse` behavior as the peer-leader card.
 */

interface Props {
  participants: PostSessionParticipant[];
  attendance: AttendanceDraftMap;
  onParticipantChange: (
    userId: string,
    next: AttendanceDraftMap[string]
  ) => void;
  onSave: () => Promise<boolean>;
  saving: boolean;
}

export function AttendanceEngagementCard({
  participants,
  attendance,
  onParticipantChange,
  onSave,
  saving,
}: Props) {
  const { isComplete, summaryLine, collapsed, setCollapsed, markDirty, save } =
    useAttendanceSectionCollapse(participants, attendance, onSave);

  // MED-1191 AC9: show the pre-fill note only when the roster actually arrived
  // pre-populated. Derived from the immutable bundle participants (not the
  // editable drafts) so it stays put while the leader reviews, and disappears
  // after a save (the server clears status_auto_populated once recorded).
  // Naturally absent in the AC7 fully-manual fallback (nothing pre-filled).
  const wasPrepopulated = participants.some(p => p.status_auto_populated);

  const handleParticipantChange = (
    userId: string,
    next: AttendanceDraftMap[string]
  ) => {
    markDirty();
    onParticipantChange(userId, next);
  };

  return (
    <ContentCard
      variant="elevated"
      padding="lg"
      fill={false}
      contentGap="md"
      header={
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Group gap={8} align="center" wrap="nowrap">
              <CheckSquare size={18} color="#1A1A2E" aria-hidden="true" />
              <Text fz={15} fw={700} c="#1A1A2E">
                Attendance &amp; Engagement
              </Text>
            </Group>
            <Text fz={12} fw={500} c="#9CA3AF">
              {collapsed
                ? summaryLine
                : 'Mark attendance, add reasons for absences, and rate engagement.'}
            </Text>
            {/* MED-1191 AC9 — pre-fill source note (only when pre-populated). */}
            {wasPrepopulated && !collapsed && (
              <Group gap={6} align="flex-start" wrap="nowrap">
                <Box style={{ flexShrink: 0, marginTop: 2 }}>
                  <Info size={13} color="#92650E" aria-hidden="true" />
                </Box>
                <Text fz={12} fw={500} c="#92650E">
                  {ATTENDANCE_PREFILL_NOTE}
                </Text>
              </Group>
            )}
          </Stack>
          {isComplete && (
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => setCollapsed(c => !c)}
              aria-label={
                collapsed ? 'Expand attendance' : 'Collapse attendance'
              }
            >
              {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </ActionIcon>
          )}
        </Group>
      }
    >
      {!collapsed && (
        <>
          <Stack gap={0}>
            {participants.map((p, idx) => (
              <Box
                key={p.user_id}
                style={
                  idx < participants.length - 1
                    ? { borderBottom: '1px solid #F3F4F6', paddingBottom: 13 }
                    : undefined
                }
                pt={idx === 0 ? 0 : 12}
              >
                <ParticipantAttendanceRow
                  userId={p.user_id}
                  firstName={p.first_name}
                  lastName={p.last_name}
                  preferredName={p.preferred_name}
                  // MED-1267 (AC22) — carry current status so a since-withdrawn
                  // (but enrolled-at-session-time) participant renders with the
                  // WITHDRAWN pill + 50% opacity.
                  participantStatus={p.participant_status}
                  draft={
                    attendance[p.user_id] ?? {
                      status: null,
                      absent_reason: null,
                      rating: null,
                      coach_notes: null,
                      autoPopulated: false,
                    }
                  }
                  onChange={next => handleParticipantChange(p.user_id, next)}
                />
              </Box>
            ))}
          </Stack>

          {/* AC2 — Save attendance moved to the bottom, right-aligned. */}
          <Group justify="flex-end">
            <Button
              size="sm"
              onClick={save}
              loading={saving}
              styles={{
                root: {
                  backgroundColor: POST_SESSION_PRIMARY_BG,
                  color: '#FFFFFF',
                },
              }}
            >
              Save Attendance
            </Button>
          </Group>
        </>
      )}
    </ContentCard>
  );
}

export default AttendanceEngagementCard;
